import { Box, IconButton, Typography } from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import { Button, Datagrid, FunctionField, ListContextProvider, useList, useRecordContext } from "react-admin";
import { settings } from "app";
import permissions from "app/permissions";
import SecuredComponent from "server/security/SecuredComponent";
import { SectionTitle } from "features/SectionTitle";
import ItemFormDialog from "../dialogs/ItemFormDialog";
import DeleteItemDialog from "../dialogs/DeleteItemDialog";
import { useIncrementalRows } from "./useIncrementalRows";
import LoadMoreButton from "./LoadMoreButton";
import { stripRowMeta, toRows } from "./rows";

const NestedRowActions = ({ singular, FormFields, items, saving, onEdit, onRemove, getItemLabel, dialogSize, formProps }) => {
  const row = useRecordContext();

  if (!row) return null;

  const item = items[row.rowIndex];

  return (
    <SecuredComponent permission={permissions.canUpdateCurriculums}>
      <Box sx={{ display: "flex", gap: 0.5, justifyContent: "flex-end" }}>
        <ItemFormDialog
          title={`Редакция · ${singular}`}
          FormFields={FormFields}
          record={item}
          onSave={data => onEdit(row.rowIndex, data)}
          dialogSize={dialogSize}
          formProps={formProps}
          triggerButton={
            <IconButton
              color="primary"
              size="small"
              aria-label="Редакция"
              disabled={saving}
            >
              <EditIcon fontSize="small" />
            </IconButton>
          }
        />
        <DeleteItemDialog
          title={`Изтриване · ${singular}`}
          itemLabel={getItemLabel?.(item)}
          onConfirm={() => onRemove(row.rowIndex)}
          triggerButton={
            <IconButton
              color="error"
              size="small"
              aria-label="Изтриване"
              disabled={saving}
            >
              <DeleteIcon fontSize="small" />
            </IconButton>
          }
        />
      </Box>
    </SecuredComponent>
  );
};

// No endpoint of its own: every change hands the parent a new array.
const NestedItems = ({
  title,
  singular,
  rowPrefix,
  items = [],
  onSave,
  saving = false,
  FormFields,
  getItemLabel,
  dialogSize = "sm",
  formProps,
  children,
}) => {
  const { visibleRows, remaining, isPending, loadMore, showAll } = useIncrementalRows(toRows(rowPrefix, items));
  const listContext = useList({ data: visibleRows });

  const addItem = data => onSave([...items, stripRowMeta(data)]);
  const editItemAt = (index, data) =>
    onSave(items.map((item, i) => (i === index ? { ...item, ...stripRowMeta(data) } : item)));
  const removeItemAt = index => onSave(items.filter((_, i) => i !== index));

  return (
    <Box sx={{ mt: 2 }}>
      <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 1 }}>
        <SectionTitle
          title={title}
          paddingY={0}
          color="secondary"
        />
        <SecuredComponent permission={permissions.canUpdateCurriculums}>
          <ItemFormDialog
            title={`Добавяне · ${singular}`}
            FormFields={FormFields}
            onSave={addItem}
            dialogSize={dialogSize}
            formProps={formProps}
            triggerButton={
              <Button
                label="Добави"
                startIcon={<AddIcon />}
                size="small"
                disabled={saving}
              />
            }
          />
        </SecuredComponent>
      </Box>

      {items.length ? (
        <>
          <ListContextProvider value={listContext}>
            <Datagrid
              bulkActionButtons={false}
              rowClick={false}
              sx={settings.style.secondaryLeftAlignedDatagrid}
            >
              {children}
              <FunctionField
                label=""
                sortable={false}
                render={() => (
                  <NestedRowActions
                    singular={singular}
                    FormFields={FormFields}
                    items={items}
                    saving={saving}
                    onEdit={editItemAt}
                    onRemove={removeItemAt}
                    getItemLabel={getItemLabel}
                    dialogSize={dialogSize}
                    formProps={formProps}
                  />
                )}
              />
            </Datagrid>
          </ListContextProvider>
          <LoadMoreButton
            remaining={remaining}
            isPending={isPending}
            onLoadMore={loadMore}
            onShowAll={showAll}
          />
        </>
      ) : (
        <Typography
          variant="body2"
          color="text.secondary"
        >
          Няма добавени записи.
        </Typography>
      )}
    </Box>
  );
};

export default NestedItems;
